import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import { format, differenceInDays, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUserPlus, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import './CRMActivityWidget.css';

export interface CRMActivityWidgetProps {
  className?: string;
}

interface RecentContact {
  id: string;
  type: 'individual';
  initials: string;
  name: string;
  contactPersonName: string; // First and last name of the person at this company/contact
  lastContactDate: Date | null;
}

function parseDate(value: string | Date | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' && value.trim() === '') return null;
  try {
    return typeof value === 'string' ? parseISO(value) : new Date(value);
  } catch {
    return null;
  }
}

/**
 * CRMActivityWidget Component
 *
 * Displays CRM activity: active clients, new this month, and most recent contacts
 * (clients + agents) sorted by contact/created date.
 */
const CRMActivityWidget: React.FC<CRMActivityWidgetProps> = ({
  className = '',
}) => {
  const { data: clients, isPending: clientsPending, error: clientsError } = useQuery({
    queryKey: ['Clients', 'dashboard'],
    queryFn: () => ClientService.getClients(),
  });

  const { data: agents } = useQuery({
    queryKey: ['Agents', 'dashboard'],
    queryFn: () => AgentsService.getAgents(),
  });

  const isPending = clientsPending;
  const error = clientsError;

  // Helper to safely extract array from response (handles wrapped responses)
  const extractArray = (data: unknown): any[] => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      if (Array.isArray(obj.data)) return obj.data;
      if (Array.isArray(obj.clients)) return obj.clients;
      if (Array.isArray(obj.agents)) return obj.agents;
    }
    return [];
  };

  // Calculate active clients and new clients this month (clients only).
  // "New This Month" is 0 when the list endpoint does not return contact_created_date.
  const clientStats = useMemo(() => {
    const clientsArray = extractArray(clients);
    if (clientsArray.length === 0) {
      return { activeClients: 0, newThisMonth: 0 };
    }
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const activeClients = clientsArray.length;
    const newThisMonth = clientsArray.filter((client: any) => {
      const createdDate = parseDate(client.ContactCreatedDate ?? client.contact_created_date);
      return createdDate != null && createdDate >= startOfMonth;
    }).length;
    return { activeClients, newThisMonth };
  }, [clients]);

  // Most recent contacts: clients + agents, sorted by date (newest first), nulls last
  const recentContacts = useMemo(() => {
    const contacts: RecentContact[] = [];

    const clientsArray = extractArray(clients);
    const agentsArray = extractArray(agents);

    clientsArray.forEach((client: any) => {
      const firstName = client.FirstName || client.first_name || '';
      const lastName = client.LastName || client.last_name || '';
      const company = client.Company || client.company || '';
      const name = (company || `${firstName} ${lastName}`.trim() || 'Unknown').trim() || 'Unknown';
      let initials = '';
      if (company) {
        initials = company.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
      } else {
        initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
      }
      const lastContactDate = parseDate(client.ContactCreatedDate ?? client.contact_created_date);
      const contactPersonName = `${firstName} ${lastName}`.trim();
      contacts.push({
        id: client.ClientID || client.client_id || '',
        type: 'individual',
        initials: initials || '??',
        name,
        contactPersonName,
        lastContactDate,
      });
    });

    (agents ?? []).forEach((agent: any) => {
      const firstName = agent.FirstName || agent.first_name || '';
      const lastName = agent.LastName || agent.last_name || '';
      const company = agent.Company || agent.company || '';
      const name = (company || `${firstName} ${lastName}`.trim() || 'Unknown').trim() || 'Unknown';
      let initials = '';
      if (company) {
        initials = company.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
      } else {
        initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
      }
      const lastContactDate = parseDate(agent.ContactCreatedAt ?? agent.contact_created_at);
      const contactPersonName = `${firstName} ${lastName}`.trim();
      contacts.push({
        id: agent.AgentID || agent.agent_id || '',
        type: 'individual',
        initials: initials || '??',
        name,
        contactPersonName,
        lastContactDate,
      });
    });

    contacts.sort((a, b) => {
      const aTime = a.lastContactDate ? a.lastContactDate.getTime() : 0;
      const bTime = b.lastContactDate ? b.lastContactDate.getTime() : 0;
      return bTime - aTime;
    });

    return contacts.slice(0, 3);
  }, [clients, agents]);

  // Format last contact date; when no date is available show a neutral label
  const formatLastContact = (date: Date | null): string => {
    if (!date) return 'In your network';

    const daysAgo = differenceInDays(new Date(), date);
    
    if (daysAgo === 0) {
      return 'Today';
    } else if (daysAgo === 1) {
      return '1 day ago';
    } else if (daysAgo < 7) {
      return `${daysAgo} days ago`;
    } else if (daysAgo < 30) {
      const weeks = Math.floor(daysAgo / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else {
      return format(date, 'MMM d, yyyy');
    }
  };

  const contactSubtitle = (contact: RecentContact): string => {
    if (contact.lastContactDate) {
      return `Last contact: ${formatLastContact(contact.lastContactDate)}`;
    }
    return contact.contactPersonName || 'In your network';
  };

  // Generate color for client initials circle
  const getInitialsColor = (initials: string): string => {
    const colors = [
      '#007bff', '#28a745', '#dc3545', '#fd7e14', '#6f42c1',
      '#20c997', '#e83e8c', '#17a2b8', '#ffc107', '#343a40',
    ];
    const index = initials.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (isPending) {
    return (
      <div className={`crm-activity-widget ${className}`}>
        <h3 className="crm-activity-title">CRM Activity</h3>
        <div className="crm-activity-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`crm-activity-widget ${className}`}>
        <h3 className="crm-activity-title">CRM Activity</h3>
        <div className="crm-activity-error">Error loading data</div>
      </div>
    );
  }

  return (
    <div className={`crm-activity-widget ${className}`}>
      <h3 className="crm-activity-title">CRM Activity</h3>

      <div className="crm-activity-content">
        {/* Summary Cards */}
        <div className="crm-activity-summary">
          <div className="crm-activity-summary-card active-clients">
            <div className="crm-activity-summary-icon">
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <div className="crm-activity-summary-content">
              <div className="crm-activity-summary-value">
                {clientStats.activeClients}
              </div>
              <div className="crm-activity-summary-label">Active Clients</div>
            </div>
          </div>

          <div className="crm-activity-summary-card new-clients">
            <div className="crm-activity-summary-icon">
              <FontAwesomeIcon icon={faUserPlus} />
            </div>
            <div className="crm-activity-summary-content">
              <div className="crm-activity-summary-value">
                {clientStats.newThisMonth}
              </div>
              <div className="crm-activity-summary-label">New This Month</div>
            </div>
          </div>
        </div>

        {/* Recent Contacts List */}
        <div className="crm-activity-contacts">
          {recentContacts.length > 0 ? (
            <>
              {recentContacts.map((contact) => (
                <Link
                  key={contact.id}
                  to={`/stakeholder/individual/${contact.id}`}
                  className="crm-activity-contact"
                >
                  <div
                    className="crm-activity-contact-avatar"
                    style={{ backgroundColor: getInitialsColor(contact.initials) }}
                  >
                    {contact.initials}
                  </div>
                  <div className="crm-activity-contact-info">
                    <div className="crm-activity-contact-name">{contact.name}</div>
                    <div className="crm-activity-contact-date">
                      {contactSubtitle(contact)}
                    </div>
                  </div>
                  <div className="crm-activity-contact-arrow">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </div>
                </Link>
              ))}
              <Link to="/sept-orbit" className="crm-activity-view-all">
                View All Contacts →
              </Link>
            </>
          ) : (
            <div className="crm-activity-empty">No recent contacts</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CRMActivityWidget;

